import { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { notificationsManager } from '@innowise-group/mui-kit';
import i18n from '@localization';

import { AuthService } from '../auth';
import { EnvConfigurationService } from '../env-configuration';
import { $api, $filesApi } from './axios';
import { KEYCLOAK_TOKEN_UPDATE_FAILED } from '../auth/auth.service';

interface CustomAxiosError<T = unknown, D = any> extends AxiosError<T, D> {
  config: RetryConfig<D>;
}

interface RetryConfig<D = any> extends InternalAxiosRequestConfig<D> {
  _retry: boolean;
}

const initBaseUrl = async () => {
  const envConfig = await EnvConfigurationService.getEnvConfig();
  $api.defaults.baseURL = envConfig.BASE_URL;
  $filesApi.defaults.baseURL = envConfig.BASE_URL;
};

const initInterceptors = () => {
  $filesApi.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    if (!config?.headers) {
      throw new Error("Expected 'config' and 'config.headers' not to be undefined");
    }
    if (AuthService.isLoggedIn()) {
      config.headers.Authorization = `Bearer ${AuthService.getToken()}`;
    }
    return config;
  });
  $filesApi.interceptors.response.use((response) => response, handleError);
  $api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    if (!config?.headers) {
      throw new Error("Expected 'config' and 'config.headers' not to be undefined");
    }
    if (AuthService.isLoggedIn()) {
      config.headers.Authorization = `Bearer ${AuthService.getToken()}`;
    }
    return config;
  });
  $api.interceptors.response.use((response) => response, handleError);
};

// A request sent before a parallel refresh has finished is rejected with the token that refresh replaced.
const isSentWithReplacedToken = (config: RetryConfig) => {
  const token = AuthService.getToken();
  return !!token && config.headers.Authorization !== `Bearer ${token}`;
};

const handleError = async (err: CustomAxiosError): Promise<CustomAxiosError> => {
  const error = err as CustomAxiosError;
  const { status } = error.response;
  const originalRequest = error.config;
  const supressNotifications = originalRequest?.ignoreGlobalCatch;

  if (status < 500) {
    if (status === 401 && originalRequest._retry) {
      AuthService.logout();

      if (!supressNotifications) {
        notificationsManager.error({
          subtitle: i18n.t('notifications.text.accessDenied'),
        });
      }

      return Promise.reject(error);
    }
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // A request that raced with a refresh only has to be repeated with the new token.
      if (!isSentWithReplacedToken(originalRequest)) {
        const updateTokenStatus = await AuthService.forceUpdateToken();

        if (updateTokenStatus === KEYCLOAK_TOKEN_UPDATE_FAILED) {
          // keycloak clears the token itself once the session is really over (refresh token rejected,
          // session ended elsewhere). A refresh request that merely failed (network) keeps the session.
          if (!AuthService.isLoggedIn()) {
            AuthService.logout();
          } else if (!supressNotifications) {
            notificationsManager.error({
              subtitle: i18n.t('notifications.text.serverError'),
            });
          }

          return Promise.reject(error);
        }
      }

      return $api(originalRequest);
    } else if (status !== 423 && !supressNotifications) {
      notificationsManager.error({
        subtitle: error.response?.data?.['message'] || error.message,
      });
    }
  } else if (!supressNotifications) {
    notificationsManager.error({
      subtitle: i18n.t('notifications.text.serverError'),
    });
  }
  return Promise.reject(error);
};

export { initInterceptors as InitInterceptors, initBaseUrl as InitBaseUrl };
